import { Link } from 'react-router-dom';
import { useCompare } from '../../contexts/CompareContext';
import { useVehicles } from '../../hooks/useVehicles';
import { getCompareVehiclesUrl } from '../../lib/routes';

export function CompareFloatingBar() {
  const { compareIds, removeFromCompare, clearCompare } = useCompare();
  const { vehicles } = useVehicles();

  if (compareIds.length === 0) return null;

  const compareVehicles = vehicles.filter(v => compareIds.includes(String(v.id)));
  const isFull = compareIds.length >= 3;
  const isReady = compareIds.length >= 2;

  return (
    <>
      {/*
        On mobile/tablet (< lg): MobileStickyBar is fixed bottom-0 at ~54px tall.
        This bar must sit above it using calc(54px + safe-area-inset).
        On lg+: MobileStickyBar is hidden, so this bar returns to bottom-0.
      */}
      <style>{`
        .compare-floating-bar {
          bottom: calc(54px + env(safe-area-inset-bottom, 0px));
        }
        @media (min-width: 1024px) {
          .compare-floating-bar { bottom: 0; }
        }
        @keyframes compareGlow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(245, 200, 66, 0.6); }
          50%       { box-shadow: 0 0 0 7px rgba(245, 200, 66, 0); }
        }
        .compare-btn-ready {
          animation: compareGlow 1.8s ease-in-out infinite;
        }
        @keyframes slotPulse {
          0%, 100% { opacity: 0.45; border-color: rgba(245,200,66,0.5); }
          50%       { opacity: 1;    border-color: rgba(245,200,66,1);   }
        }
        .slot-pulse {
          animation: slotPulse 1.5s ease-in-out infinite;
        }
      `}</style>

      <div
        className={`compare-floating-bar fixed left-0 right-0 bg-white z-50 transition-colors duration-300 ${
          isFull
            ? 'border-t-2 border-[#F5C842]'
            : 'border-t-2 border-[#E8E2D6]'
        }`}
        role="region"
        aria-label="Vehicle comparison tray"
        aria-live="polite"
      >
        {/* ── Full-state ribbon ──────────────────────────────────────────── */}
        {isFull && (
          <div
            className="flex items-center justify-center gap-2 px-4 py-1.5 bg-[#F5C842]"
            aria-live="assertive"
            aria-atomic="true"
          >
            <i className="ri-checkbox-circle-fill text-[#1A2332] text-xs" aria-hidden="true" />
            <span
              className="text-[#1A2332] text-[11px] font-black uppercase tracking-widest"
              style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.08em' }}
            >
              Tray full · 3 / 3 — Tap Compare Now or remove one to swap
            </span>
          </div>
        )}

        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">

          {/* ── Mobile layout ─────────────────────────────────────────────── */}
          <div className="flex flex-col gap-2.5 sm:hidden">

            {/* Row 1: counter pill + thumbnail strip */}
            <div className="flex items-center gap-2.5 min-w-0">

              {/* Counter pill */}
              <div
                className={`flex items-center gap-1.5 flex-shrink-0 px-2.5 py-1 rounded-full border ${
                  isFull
                    ? 'bg-[#FEF9EC] border-[#F5C842] text-[#D97706]'
                    : 'bg-transparent border-transparent text-[#1A2332]'
                }`}
              >
                <i
                  className={`text-base ${
                    isFull ? 'ri-checkbox-circle-fill text-[#F5C842]' : 'ri-scales-3-fill text-[#E8B84B]'
                  }`}
                  aria-hidden="true"
                />
                <span
                  className="text-sm font-black whitespace-nowrap"
                  style={{ fontFamily: "'Oswald', sans-serif" }}
                  aria-label={`${compareIds.length} of 3 vehicles selected for comparison`}
                >
                  {isFull ? '3 / 3' : `${compareIds.length} / 3`}
                </span>
                {isFull && (
                  <span
                    className="text-[9px] font-black uppercase tracking-wide text-[#D97706]"
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                    aria-hidden="true"
                  >
                    FULL
                  </span>
                )}
              </div>

              {/* Thumbnail strip */}
              <div
                className="flex items-center gap-2 flex-1 min-w-0 overflow-x-auto"
                style={{ scrollbarWidth: 'none' }}
              >
                {compareVehicles.map(vehicle => {
                  const imgSrc = (vehicle.images && vehicle.images.length > 0)
                    ? vehicle.images[0]
                    : vehicle.image || '';
                  return (
                    <div key={vehicle.id} className="relative flex-shrink-0">
                      <div
                        className={`rounded-lg overflow-hidden border-2 ${
                          isFull ? 'border-[#F5C842]/70' : 'border-[#E8E2D6]'
                        }`}
                        style={{ width: 56, height: 40 }}
                      >
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                            <i className="ri-car-line text-slate-400 text-sm" aria-hidden="true" />
                          </div>
                        )}
                      </div>

                      {/* Remove button — always visible on mobile */}
                      <button
                        onClick={() => removeFromCompare(String(vehicle.id))}
                        className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#1A2332] hover:bg-red-600 text-white rounded-full border-2 border-white cursor-pointer z-10 transition-colors"
                        aria-label={`Remove ${vehicle.year} ${vehicle.make} ${vehicle.model} from compare`}
                      >
                        <i className="ri-close-line text-[9px] font-black" aria-hidden="true" />
                      </button>

                      {/* Make label */}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/55 px-0.5 py-0.5 rounded-b-lg">
                        <p className="text-white text-[8px] font-semibold truncate leading-tight">
                          {vehicle.make}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* "1 slot left" pulsing empty placeholder — only when exactly 2 vehicles */}
                {compareIds.length === 2 && (
                  <div
                    className="slot-pulse relative flex-shrink-0 rounded-lg border-2 border-dashed border-[#F5C842] flex items-center justify-center cursor-default"
                    style={{ width: 56, height: 40, background: 'rgba(245,200,66,0.06)' }}
                    aria-label="1 slot left — add another vehicle to compare"
                    title="1 slot left"
                  >
                    <i className="ri-add-line text-[#F5C842] text-base" aria-hidden="true" />
                  </div>
                )}
              </div>
            </div>

            {/* Row 2: Clear + Compare Now */}
            <div className="flex items-center gap-2">
              <button
                onClick={clearCompare}
                className="px-3 py-2 text-xs font-semibold text-[#6B7A8D] hover:text-[#1A2332] transition-colors whitespace-nowrap cursor-pointer border border-[#E8E2D6] rounded-lg flex-shrink-0"
                aria-label="Clear all vehicles from comparison"
              >
                Clear All
              </button>

              {isReady ? (
                <Link
                  to={getCompareVehiclesUrl(compareIds)}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 font-black text-sm text-[#1A2332] rounded-xl transition-opacity hover:opacity-90 whitespace-nowrap ${
                    isFull ? 'compare-btn-ready' : ''
                  }`}
                  style={{
                    background: '#F5C842',
                    fontFamily: "'Oswald', sans-serif",
                    letterSpacing: '0.06em',
                  }}
                  aria-label={`Compare ${compareIds.length} selected vehicles`}
                >
                  <i className="ri-scales-3-fill" aria-hidden="true" />
                  COMPARE NOW
                </Link>
              ) : (
                <div
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 font-black text-sm text-[#1A2332]/40 rounded-xl whitespace-nowrap bg-[#F5C842]/30 cursor-not-allowed"
                  style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
                  aria-label="Add at least 2 vehicles to compare"
                  role="button"
                  aria-disabled="true"
                >
                  <i className="ri-scales-3-line" aria-hidden="true" />
                  NEED 2+
                </div>
              )}
            </div>
          </div>

          {/* ── Desktop layout ─────────────────────────────────────────────── */}
          <div className="hidden sm:flex items-center justify-between gap-4">

            {/* Left: icon + label + thumbnails */}
            <div className="flex items-center gap-3 flex-1 min-w-0">

              {/* Counter block */}
              <div
                className={`flex items-center gap-2 flex-shrink-0 px-3 py-1.5 rounded-lg border ${
                  isFull
                    ? 'bg-[#FEF9EC] border-[#F5C842]'
                    : 'bg-transparent border-transparent'
                }`}
              >
                <i
                  className={`text-2xl ${
                    isFull ? 'ri-checkbox-circle-fill text-[#F5C842]' : 'ri-scales-3-fill text-[#E8B84B]'
                  }`}
                  aria-hidden="true"
                />
                <div className="flex flex-col leading-tight">
                  <span
                    className={`font-black whitespace-nowrap text-sm ${
                      isFull ? 'text-[#D97706]' : 'text-[#1A2332]'
                    }`}
                    style={{ fontFamily: "'Oswald', sans-serif" }}
                  >
                    {isFull ? 'TRAY FULL — 3 / 3' : `COMPARE (${compareIds.length} / 3)`}
                  </span>
                  {isFull ? (
                    <span className="text-[10px] font-semibold text-[#D97706]">
                      Ready — tap Compare Now
                    </span>
                  ) : (
                    <span className="text-[10px] text-[#6B7A8D]">
                      {3 - compareIds.length} slot{3 - compareIds.length !== 1 ? 's' : ''} left
                    </span>
                  )}
                </div>
              </div>

              {/* Thumbnails */}
              <div
                className="flex items-center gap-2.5 overflow-x-auto"
                style={{ scrollbarWidth: 'none' }}
              >
                {compareVehicles.map(vehicle => {
                  const imgSrc = (vehicle.images && vehicle.images.length > 0)
                    ? vehicle.images[0]
                    : vehicle.image || '';
                  return (
                    <div key={vehicle.id} className="relative flex-shrink-0 group/thumb">
                      <div
                        className={`rounded-lg overflow-hidden border-2 transition-colors ${
                          isFull ? 'border-[#F5C842]/60' : 'border-[#E8E2D6]'
                        }`}
                        style={{ width: 80, height: 56 }}
                      >
                        {imgSrc ? (
                          <img
                            src={imgSrc}
                            alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-slate-100 flex items-center justify-center">
                            <i className="ri-car-line text-slate-400" aria-hidden="true" />
                          </div>
                        )}
                      </div>

                      {/* Remove — hover-reveal on desktop, focus-visible for keyboard */}
                      <button
                        onClick={() => removeFromCompare(String(vehicle.id))}
                        className="absolute -top-2 -right-2 w-5 h-5 flex items-center justify-center bg-[#1A2332] hover:bg-red-600 text-white rounded-full border-2 border-white cursor-pointer transition-colors opacity-0 group-hover/thumb:opacity-100 focus-visible:opacity-100"
                        aria-label={`Remove ${vehicle.year} ${vehicle.make} ${vehicle.model} from compare`}
                      >
                        <i className="ri-close-line text-[9px]" aria-hidden="true" />
                      </button>

                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent px-1 py-0.5 rounded-b-lg">
                        <p className="text-white text-[10px] font-medium truncate">
                          {vehicle.year} {vehicle.make}
                        </p>
                      </div>
                    </div>
                  );
                })}

                {/* "1 slot left" pulsing empty placeholder — only when exactly 2 vehicles */}
                {compareIds.length === 2 && (
                  <div
                    className="slot-pulse relative flex-shrink-0 rounded-lg border-2 border-dashed border-[#F5C842] flex flex-col items-center justify-center gap-1 cursor-default"
                    style={{ width: 80, height: 56, background: 'rgba(245,200,66,0.06)' }}
                    aria-label="1 slot left — add another vehicle to compare"
                    title="1 slot left — add one more"
                  >
                    <i className="ri-add-line text-[#F5C842] text-lg" aria-hidden="true" />
                    <span
                      className="text-[#F5C842] font-black leading-none"
                      style={{ fontSize: 8, fontFamily: "'Oswald', sans-serif", letterSpacing: '0.05em' }}
                      aria-hidden="true"
                    >
                      1 LEFT
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Clear + Compare Now */}
            <div className="flex items-center gap-3 flex-shrink-0">
              <button
                onClick={clearCompare}
                className="px-4 py-2 text-sm font-semibold text-[#6B7A8D] hover:text-[#1A2332] transition-colors whitespace-nowrap cursor-pointer"
                aria-label="Clear all vehicles from comparison"
              >
                Clear All
              </button>

              {isReady ? (
                <Link
                  to={getCompareVehiclesUrl(compareIds)}
                  className={`flex items-center gap-2 px-6 py-3 font-black text-sm text-[#1A2332] rounded-xl hover:opacity-90 whitespace-nowrap transition-opacity ${
                    isFull ? 'compare-btn-ready' : ''
                  }`}
                  style={{
                    background: '#F5C842',
                    fontFamily: "'Oswald', sans-serif",
                    letterSpacing: '0.06em',
                  }}
                  aria-label={`Compare ${compareIds.length} selected vehicles`}
                >
                  <i className="ri-scales-3-fill" aria-hidden="true" />
                  COMPARE NOW
                </Link>
              ) : (
                <div
                  className="flex items-center gap-2 px-6 py-3 font-black text-sm text-[#1A2332]/40 rounded-xl whitespace-nowrap bg-[#F5C842]/30 cursor-not-allowed"
                  style={{ fontFamily: "'Oswald', sans-serif", letterSpacing: '0.06em' }}
                  role="button"
                  aria-disabled="true"
                  title="Add at least 2 vehicles to compare"
                >
                  <i className="ri-scales-3-line" aria-hidden="true" />
                  ADD 2+ TO COMPARE
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
